import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Palette,
  Coins,
  Watch,
  Mail,
  Gem,
  Landmark,
  ImagePlus,
  Loader2,
  Send,
  CheckCircle2,
  X,
  ScrollText,
} from "lucide-react";
import { API } from "../App";
import Reveal from "../components/Reveal";
import { useLang } from "../i18n/i18n";

const CAT_IMGS = [
  "https://images.unsplash.com/photo-1745237826674-1fb299b72913?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "https://images.unsplash.com/photo-1657214005798-86b35114fc0b?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "https://images.unsplash.com/photo-1595923533867-ff8a01335ff9?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "https://images.unsplash.com/photo-1787741045490-7a0969d24261?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "https://images.unsplash.com/photo-1626784214536-d859187e0bd0?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "https://images.unsplash.com/photo-1516456277948-c81597a8d1be?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
];

const CAT_ICONS = [Palette, Coins, Watch, Mail, Gem, Landmark];

function SellForm() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [estimation, setEstimation] = useState(true);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 6);
    setFiles((prev) => [...prev, ...list].slice(0, 6));
    e.target.value = "";
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${API}/upload`, fd);
        paths.push(r.data.path);
      }
      await axios.post(`${API}/collection-messages`, { ...form, estimation, images: paths });
      setDone(true);
      toast.success(t("buy.successTitle"));
    } catch {
      toast.error(t("toast.sendFail"));
    } finally {
      setSending(false);
    }
  };

  const field =
    "mt-1.5 w-full rounded-lg border border-cream/15 bg-ink/70 px-4 py-3 text-cream outline-none focus:border-gold transition-colors";
  const label = "text-xs uppercase tracking-[0.2em] text-mutedc";

  if (done)
    return (
      <div data-testid="collection-success" className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-8 sm:p-10 text-center">
        <CheckCircle2 size={42} className="mx-auto text-emerald-400" strokeWidth={1.5} />
        <h3 className="mt-4 font-display text-2xl text-cream">{t("buy.successTitle")}</h3>
        <p className="mt-3 text-sm leading-relaxed text-cream/70 max-w-sm mx-auto">
          {t("buy.successPre")}
          {form.name}
          {t("buy.successMid")}
          {estimation ? t("buy.successMidEst") : t("buy.successMidMsg")}
          {t("buy.successEnd")}
          {form.phone}.
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({ name: "", email: "", phone: "", message: "" });
            setFiles([]);
          }}
          data-testid="collection-again-btn"
          className="mt-6 rounded-full border border-cream/20 px-6 py-2.5 text-sm text-cream/80 hover:border-gold hover:text-goldlight transition-all"
        >
          {t("buy.another")}
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="collection-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label}>{t("apt.name")}</label>
          <input type="text" required value={form.name} onChange={set("name")} placeholder={t("apt.namePh")} data-testid="collection-name-input" className={field} />
        </div>
        <div>
          <label className={label}>{t("apt.phone")}</label>
          <input type="tel" required value={form.phone} onChange={set("phone")} placeholder={t("apt.phonePh")} data-testid="collection-phone-input" className={field} />
        </div>
      </div>
      <div>
        <label className={label}>{t("apt.email")}</label>
        <input type="email" required value={form.email} onChange={set("email")} placeholder={t("apt.emailPh")} data-testid="collection-email-input" className={field} />
      </div>
      <div>
        <label className={label}>{t("buy.msgLabel")}</label>
        <textarea
          required
          rows="5"
          value={form.message}
          onChange={set("message")}
          placeholder={t("buy.msgPh")}
          data-testid="collection-message-textarea"
          className={`${field} resize-none leading-relaxed`}
        />
      </div>
      <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="collection-estimation-checkbox-wrap">
        <input
          type="checkbox"
          checked={estimation}
          onChange={(e) => setEstimation(e.target.checked)}
          data-testid="collection-estimation-checkbox"
          className="h-4 w-4 accent-[#D4AF37]"
        />
        <span className="text-sm text-cream/80">{t("buy.estimationChk")}</span>
      </label>

      <div>
        <label className={label}>{t("buy.photosLabel")}</label>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={addFiles} className="hidden" data-testid="collection-images-input" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="collection-add-photos-btn"
          className="mt-2 inline-flex items-center gap-2 rounded-xl border border-dashed border-gold/40 px-5 py-3.5 text-sm text-goldlight hover:bg-gold/5 transition-all"
        >
          <ImagePlus size={17} /> {t("buy.addPhotos")}
        </button>
        {files.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-3">
            {files.map((f, i) => (
              <div key={i} className="relative">
                <img src={URL.createObjectURL(f)} alt={f.name} className="h-20 w-20 rounded-lg object-cover border border-gold/25" data-testid="collection-image-preview" />
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  data-testid="collection-image-remove-btn"
                  className="absolute -top-2 -right-2 rounded-full bg-ink border border-gold/40 p-1 text-goldlight hover:text-red-300 hover:border-red-400/50 transition-all"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="collection-submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-ink transition-all hover:bg-goldlight disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {sending ? t("buy.sending") : t("buy.submit")}
      </button>
    </form>
  );
}

export default function Buy() {
  const { t } = useLang();
  const cats = CAT_IMGS.map((img, i) => ({ img, icon: CAT_ICONS[i], tt: `buy.cat${i + 1}t`, dd: `buy.cat${i + 1}d` }));

  return (
    <main className="pt-[72px]">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20" data-testid="buy-header">
        <Reveal>
          <p className="text-xs tracking-[0.35em] uppercase text-gold">{t("buy.eyebrow")}</p>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl font-light text-cream leading-tight">
            {t("buy.titlePre")}
            <em className="gold-text italic font-medium">{t("buy.titleItalic")}</em>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-cream/70">{t("buy.sub")}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20" data-testid="buy-categories">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map((c, i) => (
            <Reveal key={c.tt} delay={0.05 * i} className="h-full">
              <div className="group h-full overflow-hidden rounded-2xl border border-gold/15 bg-panel/70 transition-all hover:border-gold/40 hover:-translate-y-1 duration-300">
                <div className="h-44 overflow-hidden">
                  <img src={c.img} alt={t(c.tt)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <c.icon size={22} className="text-gold" strokeWidth={1.5} />
                  <h3 className="mt-3 font-display text-xl text-cream">{t(c.tt)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream/60">{t(c.dd)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20" data-testid="succession-section">
        <Reveal>
          <div className="rounded-3xl border border-gold/20 bg-gradient-to-r from-panel2 to-panel p-8 sm:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-8">
            <ScrollText size={34} className="text-gold shrink-0" strokeWidth={1.5} />
            <div>
              <h2 className="font-display text-2xl sm:text-3xl text-cream">{t("buy.succT")}</h2>
              <p className="mt-3 text-base leading-relaxed text-cream/70 max-w-2xl">{t("buy.succD")}</p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 grid gap-14 lg:grid-cols-2 items-start" data-testid="buy-form-section">
        <Reveal>
          <p className="text-xs tracking-[0.35em] uppercase text-gold">{t("buy.formEyebrow")}</p>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-light text-cream leading-tight">
            {t("buy.formTitleA")}
            <br />
            <em className="gold-text italic font-medium">{t("buy.formTitleB")}</em> {t("buy.formTitleC")}
          </h2>
          <p className="mt-6 text-base leading-relaxed text-cream/70 max-w-md">{t("buy.formSub")}</p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="rounded-3xl border border-gold/20 bg-panel/70 backdrop-blur p-7 sm:p-9" data-testid="collection-form-panel">
            <SellForm />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
